import React, { useState, useEffect } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { ChevronDown, ChevronRight } from 'lucide-react';
import PolishedNarrativeCard from '@/components/PolishedNarrativeCard';

// ══════════════════════════════════════════════════════════════════════════════
// BIOLOGICAL STORY — DECODE
// ══════════════════════════════════════════════════════════════════════════════

// Axis display configuration
const AXIS_CONFIG = {
  BAS: {
    label: 'Biological Adaptation',
    tint: { bg: 'rgba(78, 122, 107, 0.10)', border: 'rgba(78, 122, 107, 0.20)', text: '#4E7A6B' }
  },
  NAS: {
    label: 'Neurological Adaptation',
    tint: { bg: 'rgba(126, 107, 156, 0.06)', border: 'rgba(126, 107, 156, 0.20)', text: '#7E6B9C' }
  },
  TAS: {
    label: 'Terrain Adaptation',
    tint: { bg: 'rgba(140, 98, 57, 0.10)', border: 'rgba(140, 98, 57, 0.20)', text: '#8C6239' }
  }
};

// Status to readable description
function statusToDescription(status) {
  const map = {
    moderate_decline: 'shifting gently, worth attention',
    needs_support: 'responding well to support',
    elevated: 'a little elevated, being watched',
    needs_attention: 'gently asking for attention',
    moderate: 'within a manageable range',
    adequate: 'holding its ground',
    stable: 'holding its rhythm',
    optimal: 'settled and well-supported',
  };
  return map[status] || status?.replace(/_/g, ' ') || '';
}

// Clean lab validation text from summaries for subscriber view
function cleanSummaryText(text) {
  if (!text) return '';
  // Remove "Lab validation:" sections (internal context)
  return text.split('\n\nLab validation:')[0].trim();
}


export default function SubscriberDecode() {
  const { user } = useAuth();
  const [decode, setDecode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEvolution, setShowEvolution] = useState(false);

  const subscriberId = user?.role === 'caregiver' ? user?.linked_subscriber_id : user?.id;

  useEffect(() => {
    const fetchDecode = async () => {
      if (!subscriberId) return;
      try {
        const response = await axios.get(`${API}/decodes/${subscriberId}`);
        setDecode(response.data);
      } catch (error) {
        console.error('Error fetching DECODE:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDecode();
  }, [subscriberId]);

  if (loading) {
    return (
      <BiologicalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-bio-accent/30 border-t-bio-accent rounded-full animate-spin" />
        </div>
      </BiologicalLayout>
    );
  }

  if (!decode) {
    return (
      <BiologicalLayout>
        <div className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
          {/* Polished perception narrative — visible even before a DECODE is published */}
          <section className="mb-10" data-testid="decode-polished-narrative">
            <PolishedNarrativeCard subscriberId={subscriberId} variant="subscriber" />
          </section>

          <div className="text-center pt-4">
            <p className="text-xl font-['Outfit'] font-light text-bio-primary mb-4">
              Your biological story is being composed
            </p>
            <p className="text-sm text-bio-muted leading-relaxed max-w-md mx-auto">
              As your assessments and observations come together, your personalized biological foundation will emerge here.
            </p>
          </div>
        </div>
      </BiologicalLayout>
    );
  }

  // Manual PDF DECODE — show document viewer
  if (decode.source === 'manual_upload' && decode.document_url) {
    return (
      <BiologicalLayout>
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="decode-biological-story">
          <div className="text-center mb-8">
            <p className="text-[11px] text-bio-muted/40 uppercase tracking-[3px] mb-2 font-['Outfit']">Your Biological Foundation</p>
            <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-light text-bio-primary tracking-wide">
              360 DECODE
            </h1>
            <p className="text-xs text-bio-muted mt-2">Your personalised biological assessment report</p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
              <span className="text-xs text-white/50">{decode.filename || 'DECODE Report'}</span>
              <a href={decode.document_url} target="_blank" rel="noreferrer"
                className="text-xs text-[#C89B7B] hover:text-[#B88A6A] flex items-center gap-1">
                Open Full Report
              </a>
            </div>
            <iframe src={decode.document_url} className="w-full" style={{ height: '80vh', minHeight: '600px' }}
              title="DECODE Report" />
          </div>
          {decode.notes && (
            <div className="mt-6 p-4 bg-white/[0.02] rounded-lg">
              <p className="text-[10px] text-white/30 uppercase mb-1">Care Team Notes</p>
              <p className="text-sm text-white/60">{decode.notes}</p>
            </div>
          )}
        </div>
      </BiologicalLayout>
    );
  }

  // Extract lab sync context
  const labSync = decode.detailed_data?.lab_sync;
  const hasLabValidation = !!labSync?.last_synced;
  const labDrivers = labSync?.key_drivers || [];
  const labAxisScores = labSync?.axis_scores || {};

  // ── Backbone-driven enrichment (backward-compatible) ──
  // Only renders when the published DECODE was saved via the new workspace.
  const detailed = decode.detailed_data || {};
  const backboneAxes = Array.isArray(detailed.axis_analysis) ? detailed.axis_analysis : null;
  const backbonePriorities = Array.isArray(detailed.priorities) ? detailed.priorities : null;
  const backboneContradictions = Array.isArray(detailed.contradictions) ? detailed.contradictions : null;
  const backboneTrajectory = detailed.trajectory && typeof detailed.trajectory === 'object' ? detailed.trajectory : null;
  const hasBackboneEnrichment = !!(backboneAxes || backbonePriorities || backboneContradictions || backboneTrajectory);

  // Build the opening narrative from subscriber_facing_summary or construct one
  const openingNarrative = decode.subscriber_facing_summary ||
    `${decode.current_state ? decode.current_state + '.' : ''} ${cleanSummaryText(decode.bas_summary)?.split('.')[0]}.`;

  // Top 3 drivers only
  const topDrivers = (decode.top_drivers || []).slice(0, 3);

  return (
    <BiologicalLayout>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="decode-biological-story">

        {/* ─── Title ─────────────────────────────────────── */}
        <div className="mb-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-bio-faint mb-3 font-['Work_Sans']"
             data-testid="decode-label">
            Your Biological Foundation
          </p>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-light text-bio-primary leading-tight"
              data-testid="decode-title">
            DECODE
          </h1>
        </div>


        {/* ─── Opening Narrative ─────────────────────────── */}
        <section className="mb-12" data-testid="decode-opening-narrative">
          <p className="text-base sm:text-lg text-bio-body leading-[1.8] font-['Work_Sans'] font-light">
            {openingNarrative}
          </p>
        </section>

        {/* ─── LLM-Polished Perception Narrative (additive) ─────────── */}
        <section className="mb-16" data-testid="decode-polished-narrative">
          <PolishedNarrativeCard subscriberId={subscriberId} variant="subscriber" />
        </section>


        {/* ─── Current State ────────────────────────────── */}
        {decode.current_state && (
          <section className="mb-16" data-testid="decode-current-state">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              Where things stand
            </p>
            <p className="text-bio-strong text-sm sm:text-base leading-relaxed font-['Work_Sans']">
              {decode.current_state}
            </p>
            {decode.current_modifiable_window && (
              <p className="text-bio-subtle text-sm leading-relaxed mt-3 font-['Work_Sans']">
                {decode.current_modifiable_window}
              </p>
            )}
          </section>
        )}


        {/* ─── Backbone-driven enrichment (shown only when present) ───
            Backward-compatible: older published DECODEs without detailed_data
            skip this block entirely and fall back to the existing sections below. */}
        {hasBackboneEnrichment && (
          <BackboneEnrichmentBlock
            axes={backboneAxes}
            priorities={backbonePriorities}
            contradictions={backboneContradictions}
            trajectory={backboneTrajectory}
          />
        )}


        {/* ─── Axis Stories ─────────────────────────────── */}
        {['BAS', 'NAS', 'TAS'].map(axis => {
          const config = AXIS_CONFIG[axis];
          const summaryField = `${axis.toLowerCase()}_summary`;
          const rawSummary = decode[summaryField];
          const summary = cleanSummaryText(rawSummary);

          if (!summary) return null;

          // Get system statuses related to this axis
          const axisStatuses = [];
          if (axis === 'BAS') {
            if (decode.mitochondrial_status) axisStatuses.push({ label: 'Cellular energy', value: statusToDescription(decode.mitochondrial_status) });
            if (decode.detox_load) axisStatuses.push({ label: 'Detoxification', value: statusToDescription(decode.detox_load) });
            if (decode.gut_status) axisStatuses.push({ label: 'Gut terrain', value: statusToDescription(decode.gut_status) });
          } else if (axis === 'NAS') {
            if (decode.neuron_status) axisStatuses.push({ label: 'Neuronal health', value: statusToDescription(decode.neuron_status) });
            if (decode.inflammation_status) axisStatuses.push({ label: 'Inflammatory tone', value: statusToDescription(decode.inflammation_status) });
          } else if (axis === 'TAS') {
            if (decode.buffering_status) axisStatuses.push({ label: 'Buffering capacity', value: statusToDescription(decode.buffering_status) });
          }

          // Lab validation for this axis
          const axisLabScore = labAxisScores[axis];
          const hasAxisLab = axisLabScore && axisLabScore.markers_tested > 0;

          return (
            <section key={axis} className="mb-14" data-testid={`decode-axis-${axis}`}>
              <div
                className="rounded-2xl p-6 sm:p-8"
                style={{
                  backgroundColor: config.tint.bg,
                  border: `1px solid ${config.tint.border}`
                }}
              >
                <p className="text-[10px] uppercase tracking-[0.25em] mb-4 font-['Work_Sans']"
                   style={{ color: config.tint.text, opacity: 0.7 }}>
                  {config.label}
                </p>

                <p className="text-bio-strong text-sm sm:text-base leading-[1.85] font-['Work_Sans'] font-light">
                  {summary}
                </p>

                {/* System statuses — subtle inline */}
                {axisStatuses.length > 0 && (
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-5">
                    {axisStatuses.map((s, i) => (
                      <span key={i} className="text-[11px] text-bio-faint font-['Work_Sans']">
                        {s.label}: <span className="text-bio-muted">{s.value}</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* Lab validation — subtle note */}
                {hasAxisLab && (
                  <p className="text-[11px] text-bio-faint mt-4 font-['Work_Sans']"
                     data-testid={`decode-lab-validation-${axis}`}>
                    Grounded in {axisLabScore.markers_tested} biological marker{axisLabScore.markers_tested > 1 ? 's' : ''} from your own labs
                  </p>
                )}
              </div>
            </section>
          );
        })}


        {/* ─── Key Drivers ──────────────────────────────── */}
        {topDrivers.length > 0 && (
          <section className="mb-16" data-testid="decode-key-drivers">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              What is shaping how you feel
            </p>
            <div className="space-y-3">
              {topDrivers.map((driver, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="text-[11px] text-bio-ghost font-['Work_Sans'] mt-0.5 w-4 flex-shrink-0">
                    {i + 1}.
                  </span>
                  <p className="text-bio-body text-sm leading-relaxed font-['Work_Sans']"
                     data-testid={`decode-driver-${i}`}>
                    {driver}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}


        {/* ─── Strategic Direction ──────────────────────── */}
        {decode.strategic_direction && (
          <section className="mb-16" data-testid="decode-strategic-direction">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              How your care is being shaped
            </p>
            <p className="text-bio-strong text-sm sm:text-base leading-[1.85] font-['Work_Sans'] font-light">
              {decode.strategic_direction}
            </p>
          </section>
        )}


        {/* ─── Phase Objectives ──────────────────────────── */}
        {decode.phase_objectives && decode.phase_objectives.length > 0 && (
          <section className="mb-16" data-testid="decode-objectives">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              Current focus areas
            </p>
            <div className="space-y-2.5">
              {decode.phase_objectives.map((obj, i) => (
                <p key={i} className="text-bio-secondary text-sm leading-relaxed font-['Work_Sans'] pl-4"
                   style={{ borderLeft: '2px solid rgba(200, 155, 123, 0.25)' }}
                   data-testid={`decode-objective-${i}`}>
                  {obj}
                </p>
              ))}
            </div>
          </section>
        )}


        {/* ─── Recent Biological Validation ─────────────── */}
        {hasLabValidation && (
          <section className="mb-16" data-testid="decode-lab-validation">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              What your body has shown us lately
            </p>
            <div className="rounded-xl p-5"
                 style={{ backgroundColor: 'rgba(200, 155, 123, 0.18)', border: '1px solid rgba(200, 155, 123, 0.18)' }}>
              <p className="text-bio-muted text-sm leading-relaxed font-['Work_Sans']">
                This picture was most recently refined through {labSync.total_markers || labDrivers.length} biological marker{(labSync.total_markers || labDrivers.length) === 1 ? '' : 's'} from your recent labs.
                {labDrivers.length > 0 && (
                  <> The strongest signals we found: {labDrivers.slice(0, 3).join(', ')}.</>
                )}
              </p>
              <p className="text-[10px] text-bio-ghost mt-3 font-['Work_Sans']">
                Last updated {new Date(labSync.last_synced).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </section>
        )}


        {/* ─── Evolution History (Optional, Collapsed) ──── */}
        {decode.version > 1 && (
          <section className="mb-10" data-testid="decode-evolution">
            <button
              onClick={() => setShowEvolution(!showEvolution)}
              className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-bio-ghost hover:text-bio-faint transition-colors font-['Work_Sans']"
              data-testid="decode-evolution-toggle"
            >
              {showEvolution ? <ChevronDown className="w-3 h-3" strokeWidth={1} /> : <ChevronRight className="w-3 h-3" strokeWidth={1} />}
              Biological progression
            </button>

            {showEvolution && (
              <div className="mt-4 space-y-2 pl-5" style={{ borderLeft: '1px solid rgba(0,0,0,0.06)' }}>
                <p className="text-bio-faint text-xs font-['Work_Sans']">
                  This foundation has been refined {decode.version - 1} time{decode.version > 2 ? 's' : ''} since it was first established.
                </p>
                {hasLabValidation && (
                  <p className="text-bio-faint text-xs font-['Work_Sans']">
                    Most recent refinement included biological marker validation.
                  </p>
                )}
              </div>
            )}
          </section>
        )}


        {/* ─── Closing breath ──────────────────────────── */}
        <div className="pt-8 pb-4">
          <div className="w-8 h-px bg-bio-border mx-auto" />
        </div>

      </div>
    </BiologicalLayout>
  );
}


// ─── Subscriber-friendly mappings (backbone → human words) ───

const AXIS_FRIENDLY = {
  inflammation_axis: 'Inflammation',
  methylation_repair_axis: 'Repair & Methylation',
  metabolic_flexibility_axis: 'Metabolic Balance',
  kidney_filtration_axis: 'Kidney Reserve',
  liver_detox_axis: 'Liver & Detox',
  thyroid_adaptive_axis: 'Thyroid Rhythm',
  mineral_reserve_axis: 'Mineral Reserves',
  immune_autoimmune_axis: 'Immune Tone',
  oxidative_stress_axis: 'Oxidative Load',
  hormonal_signaling_axis: 'Hormonal Signalling',
  gut_barrier_axis: 'Gut Terrain',
  toxic_burden_axis: 'Terrain Cleanliness',
  blood_oxygenation_axis: 'Oxygenation',
};

const STATUS_WORDS = {
  critical: 'working hardest right now',
  stressed: 'feeling the pressure',
  suboptimal: 'not quite settled',
  optimal: 'resting in a good place',
  insufficient_data: 'we need a little more time to read this one',
};

const STATUS_DOT = {
  critical: '#E88B7B',
  stressed: '#D9A873',
  suboptimal: '#B8AF8E',
  optimal: '#8FB59B',
  insufficient_data: '#9CA3A5',
};

const TYPE_GENTLE = {
  root_driver: 'At the root of what we\'re seeing',
  amplifier: 'Adding to what\'s going on',
  secondary: 'In the background, worth gentle attention',
  monitor_only: 'Something to keep a quiet eye on',
};

const TRAJECTORY_PHRASE = {
  improving: 'The direction is gentle and encouraging.',
  worsening: 'A few things are drifting — your plan is being shaped to meet them.',
  mixed: 'Some parts are settling, while others are still asking for a little more time.',
  stable: 'Things are holding their rhythm — steady, without strain.',
  insufficient_data: 'We need a little more time together before we can speak to direction — your next lab review will help this come into focus.',
};


// ─── Backbone-driven enrichment section ───

function BackboneEnrichmentBlock({ axes, priorities, contradictions, trajectory }) {
  // Terrain Map: top flagged axes only
  const flaggedAxes = (axes || [])
    .filter(a => a.status === 'critical' || a.status === 'stressed' || a.status === 'suboptimal')
    .slice(0, 5);

  // What Matters Most: root_drivers + top amplifiers (max 4)
  const matters = (priorities || [])
    .filter(p => p.type === 'root_driver' || p.type === 'amplifier')
    .slice(0, 4);

  // Hidden Strain: contradictions only when present
  const hidden = (contradictions || []).slice(0, 3);

  const trajectoryStatus = trajectory?.status;
  const trajectoryPhrase = trajectoryStatus ? TRAJECTORY_PHRASE[trajectoryStatus] : null;

  // Don't render empty enrichment
  if (!flaggedAxes.length && !matters.length && !hidden.length && !trajectoryPhrase) {
    return null;
  }

  return (
    <div data-testid="decode-backbone-enrichment">

      {/* Terrain Map */}
      {flaggedAxes.length > 0 && (
        <section className="mb-16" data-testid="decode-terrain-map">
          <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
            Your terrain, at a glance
          </p>
          <div className="space-y-3">
            {flaggedAxes.map(a => (
              <div
                key={a.axis_key}
                className="flex items-center gap-4 py-2.5 px-4 rounded-xl"
                style={{
                  backgroundColor: 'rgba(200, 155, 123, 0.06)',
                  border: '1px solid rgba(200, 155, 123, 0.12)',
                }}
                data-testid={`decode-terrain-axis-${a.axis_key}`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: STATUS_DOT[a.status] || STATUS_DOT.optimal }}
                />
                <span className="text-bio-strong text-sm font-['Work_Sans'] font-light flex-1">
                  {AXIS_FRIENDLY[a.axis_key] || a.axis_name}
                </span>
                <span className="text-bio-subtle text-xs font-['Work_Sans'] italic">
                  {STATUS_WORDS[a.status] || a.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* What Matters Most */}
      {matters.length > 0 && (
        <section className="mb-16" data-testid="decode-what-matters">
          <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
            What matters most right now
          </p>
          <div className="space-y-5">
            {matters.map((p, i) => (
              <div key={i} className="flex items-start gap-4" data-testid={`decode-matters-${i}`}>
                <span className="text-[11px] text-bio-ghost font-['Work_Sans'] mt-1 w-4 flex-shrink-0 tabular-nums">
                  {i + 1}.
                </span>
                <div className="flex-1">
                  <p className="text-bio-body text-sm sm:text-base leading-relaxed font-['Work_Sans']">
                    {p.label}
                  </p>
                  <p className="text-bio-faint text-[11px] mt-1 font-['Work_Sans'] uppercase tracking-[0.15em]">
                    {TYPE_GENTLE[p.type] || 'Worth noting'}
                  </p>
                  {p.recommended_focus && (
                    <p className="text-bio-subtle text-sm leading-relaxed mt-2 font-['Work_Sans'] font-light">
                      {p.recommended_focus}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Hidden Strain / Important Watch Areas */}
      {hidden.length > 0 && (
        <section className="mb-16" data-testid="decode-hidden-strain">
          <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
            Important watch areas
          </p>
          <div className="space-y-4">
            {hidden.map((c, i) => (
              <div
                key={c.key || i}
                className="pl-4 py-1"
                style={{ borderLeft: '2px solid rgba(232, 139, 123, 0.35)' }}
                data-testid={`decode-hidden-${i}`}
              >
                <p className="text-bio-strong text-sm font-['Work_Sans'] font-light">
                  {c.label}
                </p>
                {c.summary && (
                  <p className="text-bio-subtle text-sm leading-relaxed mt-1.5 font-['Work_Sans'] font-light">
                    {c.summary}
                  </p>
                )}
              </div>
            ))}
          </div>
          <p className="text-bio-faint text-[11px] mt-5 font-['Work_Sans'] italic">
            These are gentle signals, not alarms. Your care team is already holding them in mind.
          </p>
        </section>
      )}

      {/* Direction / Trajectory */}
      {trajectoryPhrase && (
        <section className="mb-16" data-testid="decode-direction">
          <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
            Where this is heading
          </p>
          <p className="text-bio-strong text-sm sm:text-base leading-[1.85] font-['Work_Sans'] font-light">
            {trajectoryPhrase}
          </p>
        </section>
      )}
    </div>
  );
}
